/**
 * AdminTemplatesCard — single-source inventory of every messaging template
 * (WhatsApp + SMS) used across the platform, the scenario that triggers it,
 * which variables go where, and a clearly-marked **Suggested** section for
 * templates that haven't been wired yet so the admin knows what to register
 * with MSG91 / 2factor.in next.
 *
 * Lives on its own admin sub-tab to avoid crowding the existing surfaces.
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  ChatCircleText, WhatsappLogo, EnvelopeSimple, CheckCircle,
  Warning, Clock, Sparkle, CaretRight,
} from '@phosphor-icons/react';

const ChannelChip = ({ channel, provider }) => {
  const cfg = channel === 'whatsapp'
    ? { icon: WhatsappLogo, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' }
    : { icon: EnvelopeSimple, cls: 'bg-blue-100 text-blue-700 border-blue-300' };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${cfg.cls}`}
      data-testid={`tmpl-channel-${channel}`}
    >
      <Icon size={12} weight="fill" />
      {channel} · {provider}
    </span>
  );
};

const StatusChip = ({ status }) => {
  const map = {
    live:      { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'Live' },
    mocked:    { icon: Warning,     cls: 'bg-amber-100   text-amber-700   border-amber-300',   label: 'Mocked' },
    pending:   { icon: Clock,       cls: 'bg-orange-100  text-orange-700  border-orange-300',  label: 'Pending DLT' },
    suggested: { icon: Sparkle,     cls: 'bg-violet-100  text-violet-700  border-violet-300',  label: 'Suggested' },
  };
  const cfg = map[status] || map.suggested;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${cfg.cls}`}
      data-testid={`tmpl-status-${status}`}
    >
      <Icon size={12} weight="fill" />
      {cfg.label}
    </span>
  );
};

const Section = ({ title, subtitle, items, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className="mb-6 border-2 border-[#E8E1D5] rounded bg-white"
      data-testid={`tmpl-section-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-[#FAF6EE]"
        data-testid={`tmpl-section-toggle-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="text-left">
          <h3 className="font-heading text-base font-bold">{title}</h3>
          {subtitle && <p className="text-[11px] text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3 text-xs text-text-secondary">
          <span>{items.length}</span>
          <CaretRight size={14} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-[#E8E1D5] p-3 space-y-3">
          {items.map((t) => (
            <article
              key={t.name}
              className="border border-[#E8E1D5] rounded p-3 bg-[#FAF6EE]/40"
              data-testid={`tmpl-row-${t.name}`}
            >
              <header className="flex items-start justify-between gap-2 flex-wrap mb-2">
                <div>
                  <div className="font-mono text-sm font-bold break-all">{t.name}</div>
                  {t.title && (
                    <div className="text-xs text-text-secondary mt-0.5">{t.title}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <ChannelChip channel={t.channel} provider={t.provider} />
                  <StatusChip status={t.status} />
                </div>
              </header>

              <div className="text-xs space-y-1.5">
                <div>
                  <span className="font-bold text-text-secondary uppercase text-[10px] tracking-wide mr-1">
                    Trigger:
                  </span>
                  <span>{t.trigger}</span>
                </div>
                {t.body && (
                  <div className="bg-white border border-[#E8E1D5] rounded p-2 italic whitespace-pre-line"
                    data-testid={`tmpl-body-${t.name}`}>
                    {t.body}
                  </div>
                )}
                {t.vars && t.vars.length > 0 && (
                  <div className="flex items-start gap-1 flex-wrap">
                    <span className="font-bold text-text-secondary uppercase text-[10px] tracking-wide mr-1 pt-0.5">
                      Vars:
                    </span>
                    {t.vars.map((v) => (
                      <code
                        key={v.token}
                        className="text-[11px] bg-white border border-[#E8E1D5] px-1.5 py-0.5 rounded font-mono"
                      >
                        {v.token} = {v.meaning}
                      </code>
                    ))}
                  </div>
                )}
                {t.file && (
                  <div className="text-text-secondary">
                    <span className="font-bold uppercase text-[10px] tracking-wide mr-1">Code:</span>
                    <code className="font-mono">{t.file}</code>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

// ------------------------------------------------------------------
// Template inventory. Hand-maintained — when wiring a new template,
// add it here so the admin can see the full picture in one place.
// ------------------------------------------------------------------
const TEMPLATES = {
  customer: [
    {
      name: 'vendor_list_phone_no1', title: 'Vendor list + phones delivered',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Customer payment verified → vendor inquiry dispatched and phones revealed.',
      vars: [{ token: '{{var_1}}', meaning: 'vendor names list' }],
      file: 'vendor_offers_routes.py:302',
    },
    {
      name: 'profssional_phono', title: 'Professional phone delivered',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Pro confirmation fee paid → pro phone revealed to customer.',
      vars: [{ token: '{{var_1}}', meaning: 'pro / business name' }],
      file: 'vendor_offers_routes.py:339',
    },
    {
      name: 'vendor_rejection', title: 'Vendor declined booking',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Vendor declines a venue/vendor inquiry slot.',
      body:
        'Dear Customer, We regret to inform you that your booking with {{var_1}} could not be confirmed, as they are unavailable on your selected date. Please log in and choose an alternative or get refund.',
      vars: [{ token: '{{var_1}}', meaning: 'vendor / business name' }],
      file: 'vendor_offers_routes.py:378',
    },
    {
      name: 'pro_rejection', title: 'Professional declined booking',
      channel: 'whatsapp', provider: 'MSG91', status: 'pending',
      trigger: 'Pro / entertainment-pro declines an order slot.',
      body:
        'Dear Customer, We regret to inform you that your booking with {{var_1}} could not be confirmed, as they are unavailable on your selected date. Please log in to your account and choose an alternative professional or get refund.',
      vars: [{ token: '{{var_1}}', meaning: 'pro / business name' }],
      file: 'cart_notifications.send_pro_rejection',
    },
    {
      name: 'venue_saved_package_phono_delivered_is_on_somones_cart1',
      title: 'Venue is now in another cart',
      channel: 'whatsapp', provider: 'MSG91', status: 'pending',
      trigger:
        'Customer saves a package; ANOTHER customer already has the same venue + event_date. Per (customer, vendor, date) 24-hour dedupe.',
      body:
        "Dear Customer, Your shortlisted venue, {{var_1}}, is currently in another customer's cart. Please log in and finalize your booking to avoid missing out.",
      vars: [{ token: '{{var_1}}', meaning: 'venue / business name' }],
      file: 'cart_notifications.notify_cart_conflicts',
    },
    {
      name: 'pro_saved_package_phono_delivered_is_on_somones_cart1',
      title: 'Professional is now in another cart',
      channel: 'whatsapp', provider: 'MSG91', status: 'pending',
      trigger:
        'Same as the venue variant, but for any non-venue vendor / professional / entertainment-pro.',
      body:
        "Dear Customer, Your shortlisted professional, {{var_1}}, is currently in another customer's cart. Please log in and finalize your booking to avoid missing out.",
      vars: [{ token: '{{var_1}}', meaning: 'pro / business name' }],
      file: 'cart_notifications.notify_cart_conflicts',
    },
  ],
  vendorPro: [
    {
      name: 'vendor_pros_order_accept_reject', title: 'New offer awaiting accept / decline',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Customer pays → offer dispatched to vendor / pro / venue / entertainment-pro.',
      body:
        'Hi {{var_1}}, you got order {{var_2}} in beeweddy on {{var_3}}, please confirm as soon as possible.',
      vars: [
        { token: '{{var_1}}', meaning: 'recipient name' },
        { token: '{{var_2}}', meaning: 'order id' },
        { token: '{{var_3}}', meaning: 'event date' },
      ],
      file: 'vendor_offers_routes._send_offer_whatsapp',
    },
    {
      name: '(TWOFACTOR_OFFER_TEMPLATE)', title: 'Vendor offer SMS (fallback when WhatsApp is undelivered)',
      channel: 'sms', provider: '2factor.in', status: 'mocked',
      trigger: 'Same as the WhatsApp offer dispatch — fires as a parallel/fallback SMS.',
      vars: [
        { token: 'var_1', meaning: 'customer first name' },
        { token: 'var_2', meaning: 'event date' },
        { token: 'var_3', meaning: 'category / item label' },
      ],
      file: 'vendor_offers_routes._send_offer_sms',
    },
  ],
  auth: [
    {
      name: '(MSG91_TEMPLATE_ID)', title: 'OTP login SMS — MSG91 primary',
      channel: 'sms', provider: 'MSG91', status: 'live',
      trigger: 'User requests login OTP via phone.',
      body: 'BeeWeddy OTP: {{otp}} — valid for 5 min. Do not share.',
      vars: [{ token: '{{otp}}', meaning: '4-digit code' }],
      file: 'msg91_client.send_otp_sms',
    },
    {
      name: 'BWEDDYOTP (TWOFACTOR_TEMPLATE)', title: 'OTP login SMS — 2factor fallback',
      channel: 'sms', provider: '2factor.in', status: 'live',
      trigger: 'Same as MSG91 — used when MSG91 returns failure.',
      vars: [{ token: 'OTP', meaning: '4-digit code (2factor injects automatically)' }],
      file: 'server.send_otp',
    },
  ],
  marketing: [
    {
      name: 'pro_join_request', title: 'Admin invite to pro (default)',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Admin uses Auto-Blast / Marketing card to invite scraped pros.',
      vars: [{ token: '{{var_1}}', meaning: 'pro name' }],
      file: 'admin_marketing_routes._fire_one_invite',
    },
    {
      name: 'deco_join_reuest_tamil', title: 'Tamil decorator invite',
      channel: 'whatsapp', provider: 'MSG91', status: 'live',
      trigger: 'Admin Auto-Blast variant with Tamil copy.',
      vars: [{ token: '{{var_1}}', meaning: 'decorator name' }],
      file: 'admin_marketing_routes._fire_one_invite',
    },
  ],
  suggested: [
    {
      name: 'booking_confirmed_customer',
      title: 'Booking confirmed — receipt + event-day reminder hook',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'When customer\'s package payment fully settles + at least one vendor accepts.',
      body:
        'Hi {{var_1}}, your booking ({{var_2}}) for {{var_3}} on {{var_4}} is confirmed. Total ₹{{var_5}}. We\'ll remind you 24h before the event.',
      vars: [
        { token: '{{var_1}}', meaning: 'customer name' },
        { token: '{{var_2}}', meaning: 'order id' },
        { token: '{{var_3}}', meaning: 'event type' },
        { token: '{{var_4}}', meaning: 'event date' },
        { token: '{{var_5}}', meaning: 'paid total' },
      ],
    },
    {
      name: 'captain_assigned_customer',
      title: 'Captain assigned to your event',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'Admin assigns a captain to a booking — customer gets WhatsApp with captain name + phone.',
      body:
        'Hi {{var_1}}, your event captain is {{var_2}} ({{var_3}}). They\'ll coordinate on the day.',
      vars: [
        { token: '{{var_1}}', meaning: 'customer name' },
        { token: '{{var_2}}', meaning: 'captain name' },
        { token: '{{var_3}}', meaning: 'captain phone' },
      ],
    },
    {
      name: 'event_day_reminder_24h',
      title: 'T-24h event reminder',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'Cron at T-24h before event_date — fires to customer + every accepted vendor.',
      body:
        'Reminder: Your booking ({{var_1}}) is tomorrow at {{var_2}}. Vendors are confirmed. See you on event day.',
      vars: [
        { token: '{{var_1}}', meaning: 'order id' },
        { token: '{{var_2}}', meaning: 'time / venue' },
      ],
    },
    {
      name: 'review_request_post_event',
      title: 'Review request after event',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'T+1 day after event_date — asks customer to leave star rating for each vendor.',
      body:
        'Thanks for choosing BeeWeddy! How was {{var_1}}? Drop a quick rating: {{var_2}}',
      vars: [
        { token: '{{var_1}}', meaning: 'vendor name' },
        { token: '{{var_2}}', meaning: 'review deep link' },
      ],
    },
    {
      name: 'kyc_approved_vendor',
      title: 'KYC approved — vendor goes live',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'Admin approves vendor KYC documents.',
      body:
        'Congrats {{var_1}}! Your KYC is approved. Your listing is now live on BeeWeddy.',
      vars: [{ token: '{{var_1}}', meaning: 'vendor name' }],
    },
    {
      name: 'kyc_rejected_vendor',
      title: 'KYC rejected with reason',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'Admin rejects vendor KYC.',
      body:
        'Hi {{var_1}}, your KYC was not approved. Reason: {{var_2}}. Please re-upload corrected documents from your dashboard.',
      vars: [
        { token: '{{var_1}}', meaning: 'vendor name' },
        { token: '{{var_2}}', meaning: 'rejection reason' },
      ],
    },
    {
      name: 'refund_processed_customer',
      title: 'Refund processed',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger: 'Refund completes (vendor declined → wallet credit) — fires to customer.',
      body:
        'Hi {{var_1}}, ₹{{var_2}} has been credited to your BeeWeddy wallet for order {{var_3}}. Withdraw or reuse anytime.',
      vars: [
        { token: '{{var_1}}', meaning: 'customer name' },
        { token: '{{var_2}}', meaning: 'amount' },
        { token: '{{var_3}}', meaning: 'order id' },
      ],
    },
    {
      name: 'magic_link_invite_vendor',
      title: 'Admin magic-link invite',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger:
        'Currently delivered as free-form wa.me text via the Magic Link card. Wire as a DLT template for tracking + auditability.',
      body:
        "Hi {{var_1}}, you're invited to join BeeWeddy. Tap this link to set up your profile (no OTP needed, expires in 7 days): {{var_2}}",
      vars: [
        { token: '{{var_1}}', meaning: 'invitee name' },
        { token: '{{var_2}}', meaning: 'magic link URL' },
      ],
    },
    {
      name: 'cart_booked_winner_others',
      title: 'Vendor / venue is no longer available',
      channel: 'whatsapp', provider: 'MSG91', status: 'suggested',
      trigger:
        'A customer hits **Mark Booked** on a vendor + date → notify OTHER customers carrying the same vendor/date that it\'s now locked.',
      body:
        "Heads up: {{var_1}} is now booked on {{var_2}}. Update your package to pick an alternative.",
      vars: [
        { token: '{{var_1}}', meaning: 'vendor name' },
        { token: '{{var_2}}', meaning: 'event date' },
      ],
    },
  ],
};

const AdminTemplatesCard = () => {
  const counts = useMemo(() => {
    let live = 0, pending = 0, mocked = 0, suggested = 0;
    Object.values(TEMPLATES).forEach((list) => list.forEach((t) => {
      if (t.status === 'live') live += 1;
      else if (t.status === 'pending') pending += 1;
      else if (t.status === 'mocked') mocked += 1;
      else if (t.status === 'suggested') suggested += 1;
    }));
    return { live, pending, mocked, suggested };
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-templates-page">
      <Card className="bg-white border-[#E8E1D5]">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <ChatCircleText size={24} weight="duotone" />
            Message Templates &amp; Scenarios
          </CardTitle>
          <CardDescription>
            Every WhatsApp + SMS template the platform sends, the scenario that
            triggers it, and a curated <strong>Suggested</strong> set you can
            register with MSG91 / 2factor.in to close remaining gaps.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap" data-testid="tmpl-summary-counts">
            <span className="px-3 py-1 rounded bg-emerald-100 text-emerald-700 border border-emerald-300 text-xs font-bold">
              {counts.live} Live
            </span>
            <span className="px-3 py-1 rounded bg-orange-100 text-orange-700 border border-orange-300 text-xs font-bold">
              {counts.pending} Pending DLT
            </span>
            <span className="px-3 py-1 rounded bg-amber-100 text-amber-700 border border-amber-300 text-xs font-bold">
              {counts.mocked} Mocked
            </span>
            <span className="px-3 py-1 rounded bg-violet-100 text-violet-700 border border-violet-300 text-xs font-bold">
              {counts.suggested} Suggested
            </span>
          </div>
        </CardContent>
      </Card>

      <Section
        title="Customer-facing"
        subtitle="Phones revealed, vendor / pro rejections, cart conflicts."
        items={TEMPLATES.customer}
      />
      <Section
        title="Vendor / Pro-facing"
        subtitle="Order offer dispatch + SMS fallback."
        items={TEMPLATES.vendorPro}
      />
      <Section
        title="Auth"
        subtitle="OTP send paths."
        items={TEMPLATES.auth}
      />
      <Section
        title="Marketing"
        subtitle="Admin invite / auto-blast templates."
        items={TEMPLATES.marketing}
      />
      <Section
        title="Suggested — register these with MSG91 next"
        subtitle="Templates the codebase is ready to send but DLT approval is missing."
        items={TEMPLATES.suggested}
        defaultOpen={false}
      />
    </div>
  );
};

export default AdminTemplatesCard;
